import React, { useEffect, useState } from "react";
import { ReactComponent as ShieldIcon } from "./assets/shield.svg";
import classNames from "classnames";
import styles from "./InstantTournaments.module.scss";
import addTournament from "./assets/add-tournament.png";
import goldCoins from "./assets/gold-coins.svg";
import coins from "./assets/coins.png";
import players from "./assets/players.png";
import nock from "./assets/nock.svg";
import { ReactComponent as CalendarIcon } from "./assets/calendar.svg";
import { AbsoluteCenter } from "../../AbsoluteCenter";
import { AddInstantTournament } from "./AddInstantTournament";
import { loader } from "graphql.macro";
import { useQuery } from "../../../hooks/useQuery";
import {
  InstantTournamentsQuery,
  InstantTournamentsQueryVariables,
} from "../../../graphql/InstantTournamentsQuery.generated";
import { InstantTournament, ScheduledTournament } from "./types";
import { useTranslation } from "react-i18next";
import { Loader } from "../../Loader";
import { useMutation } from "../../../hooks/useMutation";
import {
  PurchaseInstantTournamentEntryMutation,
  PurchaseInstantTournamentEntryMutationVariables,
} from "./graphql/JoinInstantTournamentMutation.generated";
import { TournamentBoard } from "./TournamentBoard";
import { TournamentRounds } from "./TournamentRounds";
import {
  TableOperation,
  TournamentPlayerPerTeam,
  TournamentStatus,
  TournamentType,
} from "../../../graphql/generated";
import { useLocation, useParams } from "react-router";
import { Link, useHistory } from "react-router-dom";
import { useSubscription } from "../../../hooks/useSubscription";
import {
  InstantTournamentSubscription,
  InstantTournamentSubscriptionVariables,
} from "./graphql/InstantTournamentSubscription.generated";
import {
  InstantTournamentAuditSubscription,
  InstantTournamentAuditSubscriptionVariables,
} from "./graphql/InstantTournamentAuditSubscription.generated";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFlag, faRankingStar } from "@fortawesome/pro-solid-svg-icons";
import { JoinTournamentErrorModal } from "./JoinTournamentErrorModal";
import * as Sentry from "@sentry/browser";
import { useUser } from "../../UserProvider";

const INSTANT_TOURNAMENT_QUERY = loader(
  "../../../graphql/InstantTournamentsQuery.graphql"
);

const JOIN_INSTANT_TOURNAMENT_QUERY = loader(
  "./graphql/JoinInstantTournamentMutation.graphql"
);

const INSTANT_TOURNAMENT_SUBSCRIPTION = loader(
  "./graphql/InstantTournamentSubscription.graphql"
);

const INSTANT_TOURNAMENT_ADD_SUBSCRIPTION = loader(
  "./graphql/InstantTournamentAuditSubscription.graphql"
);

const EmptyTournamentList = ({
  onAddTournament,
  isCreationEnabled,
}: {
  onAddTournament: (val: boolean) => void;
  isCreationEnabled: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col justify-between gap-6 h-full w-full">
      <div className="flex-1 rounded-b-lg overflow-hidden bg-gray-900 relative">
        <AbsoluteCenter>
          <div className="flex flex-col gap-3 text-white items-center text-xl leading-loose	">
            <img src={addTournament} alt="vip-icon" width="100px" />
            {t("instant_tournament.no_instant_tournaments")}
            {isCreationEnabled && (
              <button
                className="btn btn-blue-600 rounded-md shadow-xl text-base text-white gap-2 px-6 py-1"
                onClick={() => onAddTournament(true)}
              >
                {t("instant_tournament.create_instant_tournament")}
              </button>
            )}
          </div>
        </AbsoluteCenter>
      </div>
    </div>
  );
};

const TournamentItem = ({
  myTournament,
  tournament,
  onJoinTournament,

  gameId,
  roomId,
}: {
  myTournament?: boolean;
  tournament: InstantTournament | ScheduledTournament;
  onJoinTournament?: (id: string) => void;
  gameId?: string;
  roomId?: string;
}) => {
  const { t } = useTranslation();
  const { push } = useHistory();
  const [joinedPlayers, setJoinedPlayers] = useState(tournament.joinedPlayers);

  const [{ data: subscriptionData }] = useSubscription<
    unknown,
    InstantTournamentSubscription,
    InstantTournamentSubscriptionVariables
  >({
    query: INSTANT_TOURNAMENT_SUBSCRIPTION,
    variables: {
      id: tournament?.id,
    },
    throwOnError: false,
    pause: !tournament.id,
  });

  useEffect(() => {
    if (
      subscriptionData?.instantTournament?.__typename ===
      "InstantTournamentEntityUpdate"
    ) {
      setJoinedPlayers(
        subscriptionData?.instantTournament.tournament.joinedPlayers
      );
    }
  }, [subscriptionData]);

  const getMaxValue = () => {
    const awards = tournament?.awards || [];
    const maxValue = Math.max(...awards!.map((a) => a.amount.value));

    return maxValue;
  };

  return (
    <div
      className={classNames(
        "flex",
        "gap-6",
        "p-5",
        "text-white",
        "items-center",
        "rounded-lg",
        styles.tournamentItem
      )}
    >
      <div className={styles.image}>
        <img
          src={`${process.env.REACT_APP_API_HOST}/instant-tournaments/${tournament.image}`}
          alt="img"
        />
      </div>
      <div className="flex flex-col gap-1 flex-grow">
        <span className="font-semibold text-lg">{tournament.name}</span>
        <div className="flex gap-2 text-sm">
          <span className={styles.prize}>{t("prize")}</span>
          <img src={goldCoins} width={24} height={24} alt="coins" />
          <span className="font-semibold">{getMaxValue()}</span>
        </div>
        <div className="flex gap-3 text-sm">
          <div className="flex items-center gap-2">
            <img src={coins} width={16} height={16} alt="coins" />
            <span className={styles.info}>{t("instant_tournament.entry")}</span>
            <span className={styles.info}>{tournament.price.value}</span>
          </div>
          <div className="flex items-center gap-2">
            <ShieldIcon width={14} height={14} />
            <span className={styles.info}>
              {tournament.playerPerTeam === TournamentPlayerPerTeam.OneVsOne
                ? "1x1"
                : "2x2"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <img src={players} width={16} height={16} alt="coins" />
            <span className={styles.info}>{`${t("players")} ${joinedPlayers}/${
              tournament.numberOfPlayers
            }`}</span>
          </div>
          {tournament.type === TournamentType.Private && (
            <div className="flex items-center gap-2">
              <img src={nock} width={16} height={16} alt="coins" />
              <span className={styles.info}>{t("private")}</span>
            </div>
          )}
        </div>
      </div>
      {tournament.status !== TournamentStatus.Completed &&
      tournament.status !== TournamentStatus.Cancelled ? (
        <div className="w-2/12 flex flex-col gap-2">
          <div className="flex flex-col gap-2">
            <span className={classNames("text-sm", styles.info)}>
              {t("instant_tournament.tournament")}
            </span>
            <span className="text-sm text-blue-600">
              {t("instant_tournament.active_now")}
            </span>
          </div>

          {tournament.type !== TournamentType.Private &&
            (joinedPlayers !== tournament.numberOfPlayers ||
              tournament.isParticipant) && (
              <button
                className="btn-green-500 rounded-md shadow-xl text-white w-full py-1 text-sm h-7"
                onClick={() => {
                  myTournament || tournament.isParticipant
                    ? push(
                        `/area/${roomId}:${gameId}/instant-tournaments/current/${tournament.id}`
                      )
                    : onJoinTournament && onJoinTournament(tournament.id);
                }}
              >
                {myTournament || tournament.isParticipant
                  ? t("instant_tournament.continue")
                  : t("join")}
              </button>
            )}
        </div>
      ) : tournament.isParticipant &&
        tournament.status !== TournamentStatus.Cancelled ? (
        <button
          className="btn-orange-600 rounded-md shadow-xl text-white w-2/12 py-1 text-sm h-7"
          onClick={() => {
            push(
              `/area/${roomId}:${gameId}/instant-tournaments/${tournament.id}`
            );
          }}
        >
          {t("instant_tournament.view_bracket")}
        </button>
      ) : (
        <></>
      )}
    </div>
  );
};

export const InstantTournaments = () => {
  const { pathname } = useLocation();
  const { user } = useUser();
  const { sectionId, tournamentId } = useParams<{
    sectionId: string;
    tournamentId: string;
  }>();
  const { t } = useTranslation();
  const { push } = useHistory();
  const [tournamentList, setTournamentList] = useState<
    InstantTournamentsQuery | undefined
  >(undefined);
  const [addTournament, setAddTournament] = useState(false);
  const [showCreateInstantButton, setShowCreateInstantButton] = useState(false);
  const [showErrorJoinModal, setShowErrorJoinModal] = useState("");

  const [
    tournamentStatus,
    setTournamentStatus,
  ] = useState<TournamentStatus | null>();

  const filterData = React.useMemo(() => {
    return {
      roomId: sectionId?.split(":")[0],
      gameId: sectionId?.split(":")[1],
    };
  }, [sectionId]);

  const [{ data, fetching }, reexecuteQuery] = useQuery<
    InstantTournamentsQuery,
    InstantTournamentsQueryVariables
  >({
    query: INSTANT_TOURNAMENT_QUERY,
    pause: !pathname?.includes("instant-tournaments"),
    variables: {
      filter: {
        roomId: filterData.roomId,
        gameId: filterData.gameId,
        onlyMine: false,
        onlyActive: true,
        statuses: [],
        playersPerTeam: [],
      },
    },
  });

  const fetchData = () => {
    reexecuteQuery({ requestPolicy: "network-only" });
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    setTournamentList(data);

    if (data?.instantTournaments?.isCreationEnabled) {
      setShowCreateInstantButton(data?.instantTournaments?.isCreationEnabled);
    }
  }, [data, showCreateInstantButton]);

  const [{ data: subscriptionData }] = useSubscription<
    unknown,
    InstantTournamentSubscription,
    InstantTournamentSubscriptionVariables
  >({
    query: INSTANT_TOURNAMENT_SUBSCRIPTION,
    variables: {
      id: tournamentId,
    },
    throwOnError: false,
  });

  useEffect(() => {
    if (subscriptionData) {
      if (
        subscriptionData.instantTournament?.__typename ===
        "InstantTournamentEntityUpdate"
      ) {
        const tournament = subscriptionData?.instantTournament?.tournament;

        setTournamentStatus(tournament?.status!);
      }
    }
  }, [subscriptionData]);

  const [{ data: subscriptionTournaments }] = useSubscription<
    unknown,
    InstantTournamentAuditSubscription,
    InstantTournamentAuditSubscriptionVariables
  >({
    query: INSTANT_TOURNAMENT_ADD_SUBSCRIPTION,
    throwOnError: false,
  });

  useEffect(() => {
    if (
      subscriptionTournaments?.instantTournaments?.operation ===
        TableOperation.Create &&
      subscriptionTournaments?.instantTournaments?.tournament
    ) {
      setTournamentList({
        instantTournaments: {
          isCreationEnabled: !!tournamentList?.instantTournaments
            ?.isCreationEnabled,
          tournaments: [
            //@ts-ignore
            {
              ...subscriptionTournaments.instantTournaments.tournament,
              isParticipant: false,
              status: TournamentStatus.Upcoming,
            },
            ...(tournamentList?.instantTournaments?.tournaments ?? []),
          ],
        },
      });
    } else if (
      subscriptionTournaments?.instantTournaments?.operation ===
        TableOperation.Update &&
      subscriptionTournaments?.instantTournaments?.tournament
    ) {
      if (
        subscriptionTournaments?.instantTournaments?.tournament?.status ===
        TournamentStatus.Cancelled
      ) {
        const newTournamentList = tournamentList?.instantTournaments?.tournaments?.filter(
          (item) =>
            item.id !==
            subscriptionTournaments?.instantTournaments?.tournament?.id
        );

        setTournamentList({
          instantTournaments: {
            isCreationEnabled: !!tournamentList?.instantTournaments
              ?.isCreationEnabled,
            tournaments: newTournamentList || [],
          },
        });
      }
    }
  }, [subscriptionTournaments]);

  useEffect(() => {
    if (tournamentId && tournamentList?.instantTournaments) {
      const currentTournament = tournamentList?.instantTournaments?.tournaments.find(
        (item) => item.id === tournamentId
      );

      setTournamentStatus(currentTournament?.status);
    }
  }, [tournamentList, tournamentList?.instantTournaments, tournamentId]);

  const [, joinInstantTournament] = useMutation<
    PurchaseInstantTournamentEntryMutation,
    PurchaseInstantTournamentEntryMutationVariables
  >(JOIN_INSTANT_TOURNAMENT_QUERY, false);

  const handleJoinTournament = (id: string) => {
    joinInstantTournament({
      input: {
        instantTournamentId: id,
        invitationId: null,
      },
    }).then((result) => {
      if (result.error) {
        setShowErrorJoinModal(result.error?.message);
        Sentry.captureMessage(
          "Error on join to instant tournament: instantTournamentId:" +
            id +
            "roomId: " +
            filterData.roomId +
            "gameId: " +
            filterData.gameId +
            "userId: " +
            user?.id
        );
        return result.error;
      } else {
        push(
          `/area/${filterData?.roomId}:${filterData.gameId}/instant-tournaments/current/${id}`
        );
      }
    });
  };

  return (
    <div className="h-full">
      <div className="h-full flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div
            className={classNames(
              "flex",
              "gap-3",
              "p-1",
              "rounded-md",
              styles.tabList
            )}
          >
            <Link
              to={`/area/${filterData?.roomId}:${filterData.gameId}/instant-tournaments`}
              className={classNames(
                "flex",
                "gap-3",
                "items-center",
                "py-1",
                "px-4",
                "rounded-md",
                styles.tab,
                styles.active
              )}
            >
              <div>
                <FontAwesomeIcon icon={faRankingStar} />
              </div>
              {t("instant_tournament.instant")}
            </Link>
            <Link
              to={`/area/${filterData?.roomId}:${filterData.gameId}/scheduled-tournaments`}
              className={classNames(
                "flex",
                "gap-3",
                "items-center",
                "py-1",
                "px-4",
                "rounded-md",
                styles.tab
              )}
            >
              <div className="flex items-center justify-between w-6 h-6">
                <CalendarIcon width={24} height={24} color="currentColor" />
              </div>
              {t("instant_tournament.scheduled")}
            </Link>
            <Link
              to={`/area/${filterData?.roomId}:${filterData.gameId}/my-tournaments`}
              className={classNames(
                "flex",
                "gap-3",
                "items-center",
                "py-1",
                "px-4",
                "rounded-md",
                styles.tab
              )}
            >
              <div>
                <FontAwesomeIcon icon={faFlag} />
              </div>
              {t("instant_tournament.my_tournaments")}
            </Link>
          </div>
          {showCreateInstantButton && (
            <button
              className="btn btn-blue-600 rounded-md shadow-xl text-white gap-2 px-6 py-1"
              onClick={() => setAddTournament(true)}
            >
              {t("instant_tournament.create_instant_tournament")}
            </button>
          )}
        </div>
        <div>
          <span className="text-white font-bold block">
            {t("instant_tournament.instant_tournaments")}
          </span>
        </div>
        {fetching ? (
          <AbsoluteCenter>
            <Loader description={t("loading_tournament")} />
          </AbsoluteCenter>
        ) : (
          <>
            {!!tournamentList?.instantTournaments?.tournaments?.length ? (
              <div
                className={classNames(
                  "flex",
                  "flex-col",
                  "flex-1",
                  "gap-2",
                  "rounded-lg",
                  "bg-gray-900",
                  "p-1",
                  "overflow-auto",
                  styles.scrollable
                )}
              >
                {tournamentList?.instantTournaments?.tournaments?.map(
                  (tournament) => (
                    <TournamentItem
                      key={tournament.id}
                      gameId={filterData.gameId}
                      roomId={filterData.roomId}
                      tournament={tournament}
                      onJoinTournament={handleJoinTournament}
                    />
                  )
                )}
              </div>
            ) : (
              <EmptyTournamentList
                onAddTournament={setAddTournament}
                isCreationEnabled={
                  !!tournamentList?.instantTournaments?.isCreationEnabled
                }
              />
            )}
          </>
        )}
      </div>
      {addTournament && (
        <AddInstantTournament
          section={{
            sectionId: sectionId.split(":")[0],
            tableId: sectionId.split(":")[1],
          }}
          closeHandler={() => {
            setAddTournament(false);
            fetchData();
          }}
        />
      )}
      {!!showErrorJoinModal && (
        <JoinTournamentErrorModal
          error={showErrorJoinModal}
          onClose={() => setShowErrorJoinModal("")}
        />
      )}
    </div>
  );
};
